import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { zh } from '../i18n';
import { cn } from '../lib/cn';
import { applyServiceWorkerUpdate, subscribeServiceWorkerUpdate } from '../lib/pwa';
import { BottomTabs } from './BottomTabs';
import {
  CredentialBanner,
  MaintenanceBanner,
  PendingRecreateBanner,
  UpdateBanner,
} from './Banners';
import { ConnectionStrip } from './ConnectionStrip';
import { Toasts } from './Toasts';

/**
 * Top glass bar + connection strip + banners + bottom tabs (SPEC §10).
 * Screens supply their own title and optional leading/trailing controls.
 *
 * `scrollable={false}` is the chat shell: the whole frame is pinned to the
 * viewport and the transcript is the only thing that scrolls. That is what
 * SPEC §10's auto-stick is written against — while the DOCUMENT was the
 * scroller, `scrollTop = scrollHeight` on the transcript moved nothing and the
 * sticky composer floated over the middle of the conversation instead of
 * sitting under it.
 */
export function AppShell({
  title,
  leading,
  trailing,
  children,
  scrollable = true,
}: {
  title?: string;
  leading?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
  scrollable?: boolean;
}): JSX.Element {
  const [updateReady, setUpdateReady] = useState(false);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => subscribeServiceWorkerUpdate(() => setUpdateReady(true)), []);

  return (
    <div className={cn('shell', !scrollable && 'shell--fixed')}>
      <a className="skip-link" href="#main">
        {zh.app.skipToContent}
      </a>
      <header className="topbar">
        <div className="topbar__row">
          {leading}
          {title ? (
            <div className="topbar__title">{title}</div>
          ) : (
            <button
              type="button"
              className="topbar__title wordmark"
              onClick={() => navigate('/')}
              aria-label={zh.app.name}
            >
              Omni<span className="wordmark__dot">.</span>
            </button>
          )}
          {trailing}
        </div>
        <ConnectionStrip />
      </header>

      <div className="shell__body">
        <BottomTabs />
        <main id="main" className={cn('shell__main', !scrollable && 'shell__main--flex')}>
          <UpdateBanner
            visible={updateReady && !updateDismissed}
            onUpdate={applyServiceWorkerUpdate}
            onDismiss={() => setUpdateDismissed(true)}
          />
          <MaintenanceBanner />
          <CredentialBanner />
          <PendingRecreateBanner />
          {children}
        </main>
      </div>
      <Toasts />
    </div>
  );
}
